import React, { useEffect, useState } from "react";
import { Input } from "antd";
import { useAllOrders } from "../../../../../api/orders/orders";
import { useLoader } from "../../../../../utils/LoaderContext";

const ORDERS_PER_PAGE = 20;

const PrepaidSection = () => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { setLoading } = useLoader();

  const {
    data: allOrdersData,
    isLoading: isAllLoading,
    isError,
    isFetching,
  } = useAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    PaymentType: "prepaid",
    search: searchQuery,
  });

  // Debounce search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  const orders = allOrdersData?.orders || [];
  const totalPages = allOrdersData?.totalPages || 1;
  const isLoading = isAllLoading;

  if (isError)
    return <p className="text-red-600 mt-4">Error fetching Prepaid orders</p>;

  return (
    <div className="w-full mt-6">
      {/* Search */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1200px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order Number / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer / Contact</th>
              <th className="p-3">Address</th>
              <th className="p-3">Courier Details</th>
              <th className="p-3">Charges</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={9} className="text-center py-6 text-gray-500">
                  Loading Prepaid orders...
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={order._id || index} className="hover:bg-gray-50">
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.order_number}
                    </div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                        order.shippingStatus === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : order.shippingStatus === "in_transit"
                          ? "bg-blue-100 text-blue-600"
                          : order.shippingStatus === "shipped"
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {order.shippingStatus}
                    </div>
                  </td>
                  <td className="p-4">
                    ₹{order.total_amount}
                    <div className="text-xs mt-1 bg-green-100 text-green-700 inline-block px-2 py-1 rounded">
                      {order.PaymentType}
                    </div>
                  </td>
                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name}
                      </p>
                    ))}
                  </td>
                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.phone}</p>
                  </td>
                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.city}</p>
                    <p>{order.BillingAddress?.state}</p>
                    <p>{order.BillingAddress?.pincode}</p>
                  </td>
                  <td className="p-4 space-y-2">
                    <p className="font-medium">{order.awbNumber || "-"}</p>
                    {/* <p>{order.PickupAddress?.WarehouseName || "-"}</p> */}
                  </td>
                    <td className="p-4 space-y-2">
                    <p className="font-medium">{order.courier_charges || "-"}₹</p>
                    {/* <p>{order.PickupAddress?.WarehouseName || "-"}</p> */}
                  </td>
                  {/* ✅ Charges Column */}
                  {/* <td className="p-4 text-sm">
                    {order.shipResponse ? (
                      <div className="space-y-1">
                        <p>
                          <span className="font-medium">Shipping:</span> ₹
                          {order.shipResponse.shipping_charges?.toFixed(2) || 0}
                        </p>
                        <p>
                          <span className="font-medium">COD:</span> ₹
                          {order.shipResponse.cod_charges?.toFixed(2) || 0}
                        </p>
                        <p className="font-semibold text-blue-600">
                          Total: ₹
                          {order.shipResponse.total_charges?.toFixed(2) || 0}
                        </p>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td> */}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="text-center py-6 text-gray-400">
                  No Prepaid orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || isFetching}
              className={`px-4 py-2 rounded border ${
                currentPage === 1 || isFetching
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || isFetching}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages || isFetching
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrepaidSection;